// Copy for the two-page Partnership Brief on the Advantage tab.
// Pricing figures on page 2 are not here — they come live from the shared
// pricing settings so they always match the Partnership page.


export const briefHeadline = {
  title: "Your growth isn't capped by sales. It's capped by recruiting.",
  lead: 'D2D HIRE builds and runs your door-to-door recruiting engine. Qualified reps start every week, and your leaders get back to leading.',
}

export const briefProblems = [
  {
    title: 'Unnecessary churn.',
    body: 'Good candidates drop off, weak ones get through, and every bad hire costs more than it should.',
  },
  {
    title: 'Leaders stuck recruiting.',
    body: 'Managers sit through unqualified interviews instead of building teams and selling.',
  },
  {
    title: "Headcount you can't forecast.",
    body: 'Hiring swings month to month, and revenue, launches and culture swing with it.',
  },
]

// One stage of the system on page 1: what happens, what D2D HIRE runs and
// what the partner's team owns.
export interface SystemStage {
  title: string
  /** Timing label (launch proposal only). */
  when?: string
  what: string
  we: string[]
  you: string[]
}

export const briefSystem: SystemStage[] = [
  {
    title: 'Attract',
    what: 'Ads that pull D2D-ready candidates, tuned on $250K a month in ad data.',
    we: ['Ads, creative and budget', 'Funnel testing and reporting'],
    you: ['Rep profile and target markets'],
  },
  {
    title: 'Screen',
    what: 'Every applicant screened live against your profile, 14 hours a day, 7 days a week.',
    we: ['Live screening', 'Interviews booked on your calendar'],
    you: ['Open interview slots'],
  },
  {
    title: 'Interview and hire',
    what: 'Structured interviews, coached weekly, so hiring calls get sharper.',
    we: ['Interview audits', 'Weekly leader coaching'],
    you: ['Running interviews', 'Every hiring decision'],
  },
  {
    title: 'Launch and retain',
    what: 'Sales School and The Forge app get new reps selling, and staying, faster.',
    we: ['Sales School for D2D HIRE reps', 'The Forge app, first 30 days'],
    you: ['Field onboarding', 'Ongoing training and culture'],
  },
]

// With a corporate recruiter, D2D HIRE runs the interviews too.
export const briefSystemWithRecruiter: SystemStage[] = [
  briefSystem[0],
  {
    ...briefSystem[1],
    we: ['Live screening', 'Interview scheduling'],
    you: ['Feedback on candidates'],
  },
  {
    ...briefSystem[2],
    what: 'Your corporate recruiter runs structured interviews; your leaders make the call.',
    we: ['Interviews, run by your recruiter', 'Weekly leader coaching'],
    you: ['Every hiring decision'],
  },
  briefSystem[3],
]

export const briefStepsTagline =
  'We run your recruiting, so you can run your business.'

export const briefTrackRecord = [
  { value: '10,000+', label: 'D2D reps recruited' },
  { value: '2004', label: 'recruiting D2D reps since' },
  { value: '43', label: 'states' },
  { value: '12', label: 'D2D industries' },
]

export const briefOrigin = {
  title: 'We built this for our own company first.',
  body: 'Peter and Jake Swenson have recruited D2D reps since 2004. Their solar company grew to 12 markets in 7 states on this exact engine. In 2025 they left solar to run it for partners.',
  proofLabel: "Inside the founders' solar company",
  proof: [
    { value: '2,000+', label: 'reps hired off job boards' },
    { value: '$130M+', label: 'in sales from those reps' },
  ],
}

export const briefSalesSchool = {
  eyebrow: 'Sales School · Included with every D2D HIRE rep',
  title: "Your new reps don't just show up. They show up ready to sell.",
  body: 'The same playbook that had first-time rookies selling roughly 90% of $130M+. Your leaders coach producers instead of starting from zero.',
  stats: [
    { value: '2 days', label: 'live sales training' },
    { value: '30 days', label: 'of The Forge app in the field' },
    { value: '$0', label: 'for every D2D HIRE-sourced rep' },
  ],
  url: 'https://d2dhire.com/sales-school',
  linkLabel: 'd2dhire.com/sales-school',
  qrCaption: 'Scan to watch the trailer',
}



// Label for the monthly service figure when the recruiter is included, so the
// note adds no extra line.
export const briefRecruiterServiceLabel = 'D2D HIRE service + recruiter'

export const briefProtection = [
  {
    title: '30-day money-back guarantee',
    body: "Not satisfied in the first 30 days from the day your first ads go live? 100% of every D2D HIRE fee paid back, including what's paid at signing.",
    // With a leader search, the clock starts with the search ads and the
    // refund covers the search months too.
    bodyWithLeader:
      'Not satisfied in the first 30 days from the day your first ads go live (the leader search)? 100% of every D2D HIRE fee paid back, including signing and the search.',
  },
]

// The hire count is filled in live from the same calculation as the
// Partnership page's guarantee card: the rep campaign's modeled ad spend in
// Months 1–3 (no leader search) ÷ the top of the cost-per-hire range,
// rounded down.
export const briefPerformanceGuarantee = {
  title: '90-day performance guarantee',
  // With a leader search first, the 90 days start when the standard
  // campaign's ads go live, not at signing.
  body: (hires: number, afterLeaderSearch = false) =>
    afterLeaderSearch
      ? `${hires} qualified ${hires === 1 ? 'hire' : 'hires'} in the first 90 days from the day your rep campaign launches, or D2D HIRE's fees pause until we hit it.`
      : `${hires} qualified ${hires === 1 ? 'hire' : 'hires'} in the first 90 days, or D2D HIRE's fees pause until we hit it.`,
}

// Third protection item: D2D HIRE's upside depends on reps who stay. The
// flagship model adds the day 45 and day 90 retention bonuses.
export const briefRetentionUpside = {
  title: 'Our upside is tied to retention',
  body: (hasRetentionBonuses: boolean) =>
    hasRetentionBonuses
      ? 'Our bonuses are earned only on reps who are still active in the field 15 and 45 days after they start knocking.'
      : 'Our bonus is earned only on reps who are still active in the field 15 days after they start knocking.',
}

export const briefProtectionNote =
  'Ad spend and your internal costs stay with you.'

export const briefTimeline = [
  {
    window: 'Implementation',
    phase: '9–30 days',
    items: [
      'Company and role profiling',
      'ATS, funnel and ads built',
      'Comp benchmarking',
      '3–4 hrs of team alignment',
    ],
  },
  {
    window: 'Month 1',
    phase: 'Launch',
    items: [
      'Ads live, screening starts',
      'First interviews and hires',
      'Baseline KPIs set',
      'Guarantees begin',
    ],
  },
  {
    window: 'Months 2–3',
    phase: 'Optimize and scale',
    items: [
      'Conversion and retention climb',
      'Weekly leader coaching',
      'Retention tracked to day 90',
    ],
  },
  {
    window: 'Month 4+',
    phase: 'Predictable engine',
    items: [
      'Forecastable hiring output',
      'Scale by moving ad spend',
      'Continuous optimization',
    ],
  },
]

// With "Find a leader" on, the timeline stays four columns: the leader search
// takes the second slot, and Months 2–3 and Month 4+ fold into one column.
export const briefLeaderPhase = {
  window: 'Months 1–2',
  phase: 'Leader search',
  items: [
    'Outreach and interviews vetted by D2D HIRE',
    'Search ads run at $2,500/mo',
    'Standard campaign starts once the leader signs',
  ],
}

export const briefLeaderLaunchPhase = {
  window: 'Month 3',
  phase: 'Launch',
  items: [
    'Ads live, screening starts',
    'First interviews and hires',
    'Guarantees begin',
  ],
}

export const briefLeaderScalePhase = {
  window: 'Month 4+',
  phase: 'Optimize and scale',
  items: [
    'Conversion and retention climb',
    'Weekly leader coaching',
    'Scale by moving ad spend',
  ],
}

export const briefCaseStudies = {
  eyebrow: 'Testimonials and case studies',
  // Each sentence gets its own line so the headline never leaves a stray word.
  titleLines: ["Don't take our word for it.", 'Hear it from our partners.'],
  body: 'Real results from D2D teams we recruit for, across industries and markets.',
  url: 'https://d2dhire.com/case-studies/',
  linkLabel: 'd2dhire.com/case-studies',
  qrCaption: 'Scan for case studies',
}

// Page 2 pricing table: ad spend tiers, our monthly service and the
// projected output at each.
export const briefPricingTable = {
  adSpendHead: 'Ad spend / mo',
  serviceHead: 'D2D HIRE service / mo',
  serviceHeadRecruiter: 'Service + recruiter / mo',
  hiresHead: 'Projected hires / mo',
  costHead: 'All-in cost per hire',
  yourPlan: 'Your plan',
  bonus: '+ $500 placement bonus per rep active at day 15 in the field',
  bonusFlagship: '+ $500 at day 15 and $1,000 at day 45, per rep in the field',
  adsNote: 'Ads are paid directly to job platforms, not D2D HIRE',
}
